import React, { useState } from 'react';
import { Play, Flame } from 'lucide-react';
import {
  getYouTubeVideoId,
  getYouTubeEmbedUrlFromUrl,
  getBunnyStreamEmbedUrl,
  getTynkEmbedUrl,
} from '@/lib/videoUtils';

type Provider = 'youtube' | 'bunny' | 'tynk';

interface ResolvedEmbed {
  provider: Provider;
  embedUrl: string;
}

/**
 * Onboarding only supports YouTube, Bunny Stream, and Tynk.
 * This is intentionally stricter than the Ensino allowlist — keeps the
 * onboarding pipeline simple and safe.
 */
export function resolveOnboardingEmbed(url: string | null | undefined): ResolvedEmbed | null {
  if (!url) return null;
  const yt = getYouTubeEmbedUrlFromUrl(url);
  if (yt) return { provider: 'youtube', embedUrl: yt };
  const bunny = getBunnyStreamEmbedUrl(url);
  if (bunny) return { provider: 'bunny', embedUrl: bunny };
  const tynk = getTynkEmbedUrl(url);
  if (tynk) return { provider: 'tynk', embedUrl: tynk };
  return null;
}

function withAutoplay(provider: Provider, embedUrl: string): string {
  try {
    const u = new URL(embedUrl);
    if (provider === 'youtube') {
      u.searchParams.set('autoplay', '1');
      // Some browsers require muted to allow autoplay even after user gesture
      // when the iframe just mounted. Keep sound on; if blocked, user clicks
      // the play button inside the player.
    } else if (provider === 'bunny') {
      u.searchParams.set('autoplay', 'true');
    }
    return u.toString();
  } catch {
    return embedUrl;
  }
}

function getBunnyThumbnail(embedUrl: string): string | null {
  try {
    const u = new URL(embedUrl);
    // Expect /embed/{libraryId}/{videoId}
    const parts = u.pathname.split('/').filter(Boolean);
    const idx = parts.indexOf('embed');
    if (idx === -1) return null;
    const libraryId = parts[idx + 1];
    const videoId = parts[idx + 2];
    if (!libraryId || !videoId) return null;
    return `https://vz-${libraryId}.b-cdn.net/${videoId}/thumbnail.jpg`;
  } catch {
    return null;
  }
}

interface LazyEmbedPlayerProps {
  videoUrl: string;
}

/**
 * Renders a thumbnail with a Play overlay; only mounts the heavy iframe
 * after the user clicks. Strictly limited to YouTube, Bunny Stream, and Tynk.
 */
export function LazyEmbedPlayer({ videoUrl }: LazyEmbedPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  const resolved = resolveOnboardingEmbed(videoUrl);

  if (!resolved) {
    return (
      <div className="aspect-video w-full rounded-xl bg-secondary/40 border border-border/50 flex items-center justify-center text-sm text-muted-foreground p-4 text-center">
        URL não suportada. Use YouTube, Bunny Stream ou Tynk.
      </div>
    );
  }

  if (playing) {
    const finalUrl = withAutoplay(resolved.provider, resolved.embedUrl);
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black">
        <iframe
          src={finalUrl}
          className="absolute inset-0 w-full h-full block"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen; web-share"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
          style={{ border: 0 }}
        />
      </div>
    );
  }

  const ytId = resolved.provider === 'youtube' ? getYouTubeVideoId(videoUrl) : null;
  const bunnyThumb = resolved.provider === 'bunny' ? getBunnyThumbnail(resolved.embedUrl) : null;
  const thumbUrl = !thumbFailed
    ? ytId
      ? `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`
      : bunnyThumb
    : null;

  return (
    <button
      type="button"
      onClick={() => setPlaying(true)}
      className="group relative aspect-video w-full overflow-hidden rounded-xl bg-secondary/50 border border-border/50 focus:outline-none focus:ring-2 focus:ring-primary"
      aria-label="Reproduzir vídeo"
    >
      {thumbUrl ? (
        <img
          src={thumbUrl}
          alt=""
          loading="lazy"
          onError={() => setThumbFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-primary/10 to-secondary flex items-center justify-center">
          <Flame className="w-16 h-16 md:w-20 md:h-20 text-primary/40" />
        </div>
      )}
      <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
          <Play className="w-8 h-8 md:w-10 md:h-10 text-primary-foreground fill-current ml-1" />
        </div>
      </div>
    </button>
  );
}